import { Icon } from '@/components/r5/glyphs'
import { R5Button } from '@/components/r5/R5Button'
import { R5Link } from '@/components/r5/R5Link'
import { R5Logo } from '@/components/r5/R5Logo'
import { currentLanguage, t, useLanguage } from '@/lib/i18n'
import type { MyTasks } from './tasks-api'
import { formatTaskDate } from './tasks-format'
import { lockTargetPath, type LockNotice } from './tasks-model'
import './tasks.css'

/** The sentence the server sends with its 409 (`practice_locked`), in the student's language. */
export function lockSentence(reason: LockNotice['reason']): string {
  return reason === 'onboarding'
    ? t('Your teacher has set a welcome test to finish first. Your other practice opens when it is done.')
    : t('Your teacher has set a task to finish first. Your other practice opens when it is done.')
}

/**
 * The page a locked test opens (ASSIGNMENTS-SPEC 11.5): why this test is closed for now, what finishes it, when it is due,
 * and the way on. It stands alone in an `r5` frame because the exam route has no shell. The exam route is English at the
 * document level (the runner stays English, Law 5), so this page sets its own language and direction: a student who reads
 * Arabic is told in Arabic, and nothing inside the runner changes.
 */
export function TaskLockNotice({ notice, data }: { notice: LockNotice; data: MyTasks }) {
  useLanguage()
  const arabic = currentLanguage() === 'ar'
  const onboarding = notice.reason === 'onboarding'
  return (
    <div className="r5 r5-shell tk-gate" dir={arabic ? 'rtl' : 'ltr'} lang={arabic ? 'ar' : 'en'} data-density="comfy">
      <main id="main" className="r5-main tk-gate__main" tabIndex={-1}>
        <div className="tk-gate__top">
          <R5Logo to="/home" />
          <R5Button asChild variant="link" size="sm" className="tk-gate__back"><R5Link to="/home">{t('Back to Home')}</R5Link></R5Button>
        </div>
        <section className="r5-card tk-gate__card" aria-labelledby="tk-gate-title">
          <span className="tk-gate__icon" aria-hidden="true"><Icon name="lock" size={22} /></span>
          <h1 className="tk-gate__title" id="tk-gate-title">{onboarding ? t('Finish your welcome test first') : t('Finish your task first')}</h1>
          <p className="tk-gate__body">{lockSentence(notice.reason)}</p>
          {notice.dueAt && <p className="tk-gate__due">{t('Due {0}', { 0: formatTaskDate(notice.dueAt) })}</p>}
          <div className="tk-gate__actions">
            <R5Button asChild variant="accent" size="lg"><R5Link to={lockTargetPath(data)}>{t('Go to your task')}</R5Link></R5Button>
            <R5Button asChild variant="ghost" size="lg"><R5Link to="/exams-library">{t('Back to the library')}</R5Link></R5Button>
          </div>
        </section>
      </main>
    </div>
  )
}
